import { formatDate, formatShortDate } from '@plato/ui-legacy/lib/date-helpers';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { Typography } from '@plato/ui/primitives/typography';
import { isToday, isTomorrow, isYesterday } from 'date-fns';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** Date over weekday on the axis; one caption row once the feed is too narrow for one. */
function DayLabel({ date }: { date?: Date }): ReactElement {
    const { t } = useTranslation();
    const locale = useResolvedLocale();

    const getRelative = (day: Date): string | undefined => {
        if (isToday(day)) {
            return t('today');
        }
        if (isYesterday(day)) {
            return t('yesterday');
        }
        if (isTomorrow(day)) {
            return t('tomorrow');
        }
        return undefined;
    };

    const relative = date && getRelative(date);
    const primary = date
        ? (relative ?? formatShortDate(date, locale))
        : t('day__no_due_date', { ns: 'activity-feed' });
    const secondary =
        date && (relative ? formatShortDate(date, locale) : formatDate(date, locale, { weekday: 'long' }));

    return (
        <div className="flex items-baseline gap-cozy @lg/feed:flex-col @lg/feed:items-start @lg/feed:gap-0">
            <Typography
                variant="body"
                className="font-medium tabular-nums text-content-weaker @lg/feed:text-content"
            >
                {primary}
            </Typography>
            {secondary && <Typography variant="caption">{secondary}</Typography>}
        </div>
    );
}

export type ActivityDayProps = {
    /** Missing for open tasks without a due date. */
    date?: Date;
    children: ReactNode;
};

/**
 * One day of a feed: the date in a left column and the day's cards beside it.
 * Needs an `@container/feed` ancestor, which switches it to one caption row when narrow.
 */
export function ActivityDay({ date, children }: ActivityDayProps): ReactElement {
    return (
        <div className="flex flex-col gap-cozy pt-relaxed first:pt-0 @lg/feed:grid @lg/feed:grid-cols-[6rem_1fr] @lg/feed:gap-x-relaxed @lg/feed:border-t @lg/feed:border-line-weakest @lg/feed:pb-relaxed @lg/feed:first:border-t-0 @lg/feed:last:pb-0">
            <DayLabel date={date} />
            <div className="flex min-w-0 flex-col gap-cozy">{children}</div>
        </div>
    );
}
